import React from 'react';
import { RefreshCw } from 'lucide-react';
import LoanPageTemplate from '../../components/LoanPageTemplate';

const Refinance = () => (
  <LoanPageTemplate
    guides={[
      { title: 'When Should You Refinance?', href: '/blog/when-to-refinance', blurb: 'Break-even maths, break costs and the signs it is time to switch lenders.' },
      { title: 'Fixed vs Variable Rates', href: '/blog/fixed-vs-variable', blurb: 'How fixed, variable and split loans compare, and what break costs mean for you.' },
      { title: 'Mortgage Broker vs Bank', href: '/blog/broker-vs-bank', blurb: 'What a broker does, how brokers are paid and when going direct makes sense.' },
    ]}
    icon={RefreshCw}
    path="/refinance"
    title="Refinance Your Home Loan"
    subtitle="Switch to a better rate and potentially save thousands over the life of your loan. We compare your current deal against a wide range of products."
    description="Australian homeowners overpay on their mortgage by an average of $3,000 per year simply because they never review their rate. Refinancing replaces your existing loan with a new one, ideally at a lower interest rate, better features, or both. We compare your current deal against a wide range of products from 80+ lenders and handle the entire switching process from application to settlement."
    features={[
      {
        title: 'Rate Comparison Across 80+ Lenders',
        description: 'We benchmark your existing rate against the full market including major banks, second-tier lenders, and non-bank specialists to find you the most competitive deal.'
      },
      {
        title: 'Cashback & Switching Incentives',
        description: 'Many lenders offer cashback rebates of $2,000 to $10,000 for refinancing customers. We identify current offers and factor them into the true cost comparison.'
      },
      {
        title: 'Debt Consolidation',
        description: 'Roll credit cards, personal loans, and car finance into your home loan at a much lower interest rate. We calculate whether consolidation saves you money after accounting for the longer repayment term.'
      },
      {
        title: 'Fixed vs Variable Analysis',
        description: 'We model different rate scenarios to help you decide between fixing your rate for certainty, staying variable for flexibility, or splitting your loan to get both.'
      },
      {
        title: 'Break Cost Assessment',
        description: 'If your current loan is on a fixed rate, break costs can be significant. We obtain exact break cost quotes from your lender and compare them against the long-term savings of switching.'
      },
      {
        title: 'Loyalty Tax Check',
        description: 'Existing customers often pay higher rates than new borrowers at the same bank. We negotiate with your current lender first and refinance only if they refuse to match the market.'
      }
    ]}
    process={[
      {
        title: 'Current Loan Health Check',
        description: 'We review your existing loan including rate, fees, features, and remaining term. You will see exactly how your deal compares to what is available today.'
      },
      {
        title: 'Savings Calculation',
        description: 'We model your potential monthly and lifetime savings across multiple lender options, accounting for any exit fees, establishment fees, and government charges.'
      },
      {
        title: 'Application & Valuation',
        description: 'We submit your application to the chosen lender, manage the property valuation, and handle all documentation. Most refinances settle within 4-6 weeks.'
      },
      {
        title: 'Settlement & Discharge',
        description: 'Your new lender pays out your old loan at settlement. We coordinate the discharge of your existing mortgage and confirm your new repayment schedule.'
      }
    ]}
    faqs={[
      {
        q: 'How much can I save by refinancing my home loan?',
        a: 'Savings depend on your loan size, current rate, and the new rate you qualify for. On a $500,000 loan, reducing your rate by 0.50% saves approximately $160 per month or $57,000 over a 30-year term. We provide a personalised savings calculation before you commit to anything.'
      },
      {
        q: 'Are there costs involved in refinancing?',
        a: 'Common costs include a discharge fee from your current lender (typically $150-$350), government registration fees ($150-$300 depending on your state), and the new lender\'s establishment or application fee. Many lenders waive upfront fees for refinancing customers. We outline all costs so you can see the net benefit.'
      },
      {
        q: 'Will refinancing affect my credit score?',
        a: 'A refinance application generates a credit enquiry on your file, which may cause a minor temporary dip in your score. However, if you are making repayments on time and not applying with multiple lenders simultaneously, the impact is minimal. We submit one targeted application to maximise your approval chances.'
      },
      {
        q: 'Can I refinance if I have less than 20% equity?',
        a: 'Yes, but your options may be more limited and you might need to pay Lenders Mortgage Insurance again. If your property has increased in value since you purchased, you may have more equity than you think. We order an upfront valuation estimate to confirm your position before proceeding.'
      },
      {
        q: 'How often should I review my home loan?',
        a: 'We recommend reviewing your mortgage at least once a year or whenever the Reserve Bank of Australia changes the cash rate. Lenders frequently adjust their pricing, and the product that was competitive two years ago may no longer be the best option. We offer ongoing rate monitoring for all our clients.'
      }
    ]}
    loanType="Refinance"
    color="from-[#00594C] to-[#003d35]"
  />
);

export default Refinance;
